import { useMemo, KeyboardEvent, MouseEvent } from 'react';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { TableState } from '../TableState';
import { TableRegions } from '../TableRegions';
import { BaseTableProps, hasSelectionColumn, BaseTableColumnProps } from './internal';
import cn from '../Table.css.json';

export interface DefaultAction {
  onClick: (event: MouseEvent) => void;
  onKeyUp: (event: KeyboardEvent) => void;
  narration: string | undefined;
}

export interface DefaultActionProps {
  columns: readonly Readonly<BaseTableColumnProps>[];
  rowCount: BaseTableProps['rowCount'];
  selectionMode: BaseTableProps['selectionMode'];
  initialFocus: TableState['initialFocus'];
  isList: boolean;
  hideSelectionControlColumn?: boolean;
  /**
   * Fired when a default action activated via click or the <code>Enter</code> key.
   * @param rowIndex the index of the row
   * @param columnIndex the index of the column
   * @param columnKey the key of the column
   */
  onActivate?: (
    rowIndex: number,
    columnIndex: number,
    columnKey?: string,
    isClick?: boolean
  ) => void;
}

export const useDefaultAction = (props: DefaultActionProps): DefaultAction => {
  const { columns, rowCount, selectionMode, initialFocus, onActivate } = props;
  const localize = useNCTextLocalizer('Table');

  const cellInfo = useMemo(() => {
    const rowIndex = initialFocus?.rowIndex ?? 0;
    const colIndex = initialFocus?.colIndex ?? 0;
    const columnKey = columns[colIndex] && columns[colIndex].key;
    const firstDataColIndex = hasSelectionColumn(selectionMode, columns.length, rowCount) ? 1 : 0;
    const getRegionForCellPosition = () => {
      if (rowIndex < 0 && colIndex < 0) {
        return TableRegions.ColumnHeaderButton;
      } else if (colIndex < firstDataColIndex) {
        return rowIndex < 0 ? TableRegions.ColumnRowHeader : TableRegions.RowHeader;
      } else if (rowIndex < 0) {
        return TableRegions.ColumnHeader;
      }

      return TableRegions.Data;
    };
    const region = getRegionForCellPosition();

    return {
      rowIndex,
      colIndex,
      region,
      columnKey,
      column: columns[colIndex],
    };
  }, [selectionMode, initialFocus, columns, rowCount]);

  const cellActivated = useMemo(() => {
    const { region, column } = cellInfo;
    const hasInteraction = !!(
      (
        column?.onPress ||
        (column?.containsInteractiveContent && !column?.onActivate && !column?.activateOnEnter)
      )
      // a column already has an interaction if containsInteractiveContent is true
      // and activateOnEnter is false and onActivate is undefined for the column,
      // which means Enter press will put the cell into edit mode
    );

    return (
      props.onActivate &&
      (region === TableRegions.Data || region === TableRegions.RowHeader) &&
      !hasInteraction
    );
  }, [props.onActivate, cellInfo]);

  const { rowIndex, colIndex, columnKey, column } = cellInfo;

  const onEvent = (event?: KeyboardEvent) => {
    // activate cell in list and table on Enter
    if (cellActivated && (!event || (event.key === 'Enter' && !event.ctrlKey && !event.metaKey))) {
      onActivate?.(rowIndex, colIndex, columnKey);
    }
  };

  const onClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const isCellArea = target.matches(`.${cn.gridCellContentContainer}, .${cn.gridCellContent}`);

    // If a cell has interactive content or a list cell uses onPress
    // only fire onActivate if the user is clicking on an empty part of the cell.
    if (column?.containsInteractiveContent || column?.onPress) {
      if (isCellArea) {
        onActivate?.(rowIndex, colIndex, columnKey, true);
      }
    } else {
      onActivate?.(rowIndex, colIndex, columnKey, true);
    }
  };

  // use the list version of the activate message even for table
  // because we're no longer using ctrl+enter to activate a table row
  const narration = cellActivated ? localize('activate.list.msg') : undefined;

  return {
    onClick,
    onKeyUp: onEvent,
    narration,
  };
};
